import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Input, Select, Space, Typography, message } from 'antd';
import { CalendarOutlined, CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import EventList from '../components/EventList';
import PullToRefresh from '../components/PullToRefresh';
import eventService from '../services/eventService';

const FILTER_STORAGE_KEY = 'pwa_active_events_filters_v1';

const readStoredFilters = () => {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) {
      return { eventTypeIds: [], eventInvolvementIds: [], eventStatusIds: [] };
    }
    const parsed = JSON.parse(raw);
    return {
      eventTypeIds: Array.isArray(parsed.eventTypeIds) ? parsed.eventTypeIds : [],
      eventInvolvementIds: Array.isArray(parsed.eventInvolvementIds) ? parsed.eventInvolvementIds : [],
      eventStatusIds: Array.isArray(parsed.eventStatusIds) ? parsed.eventStatusIds : []
    };
  } catch {
    return { eventTypeIds: [], eventInvolvementIds: [], eventStatusIds: [] };
  }
};

const writeStoredFilters = (filters) => {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Local storage is best effort.
  }
};

const toOption = (item) => ({
  label: item.name,
  value: String(item.id),
  eventTypeId: item.eventTypeId
});

const Events = () => {
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [lookups, setLookups] = useState(null);
  const [filters, setFilters] = useState(readStoredFilters);
  const eventListRef = useRef(null);

  const loadLookups = useCallback(() => eventService.getLookups()
    .then((data) => {
      setLookups(data);
    })
    .catch((error) => {
      console.error('Failed to load event lookups:', error);
      message.error('Failed to load event filters');
    }), []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const handleRefresh = useCallback(() => Promise.all([
    loadLookups(),
    eventListRef.current?.refresh()
  ]), [loadLookups]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setAppliedQuery(query.trim());
    }, 500);

    return () => window.clearTimeout(handle);
  }, [query]);

  const eventTypeOptions = useMemo(
    () => (lookups?.eventTypes || []).map(toOption),
    [lookups]
  );

  const selectedTypeSet = useMemo(
    () => new Set((filters.eventTypeIds || []).map(String)),
    [filters.eventTypeIds]
  );

  const involvementOptions = useMemo(
    () => (lookups?.eventInvolvements || [])
      .filter((item) => selectedTypeSet.size === 0 || selectedTypeSet.has(String(item.eventTypeId)))
      .map(toOption),
    [lookups, selectedTypeSet]
  );

  const statusOptions = useMemo(
    () => (lookups?.eventStatuses || [])
      .filter((item) => selectedTypeSet.size === 0 || selectedTypeSet.has(String(item.eventTypeId)))
      .map(toOption),
    [lookups, selectedTypeSet]
  );

  const eventInvolvementFilters = useMemo(() => {
    const selectedInvolvements = filters.eventInvolvementIds || [];
    if (selectedInvolvements.length === 0) return [];
    const byType = new Map();

    selectedInvolvements.forEach((id) => {
      const lookup = (lookups?.eventInvolvements || []).find((item) => String(item.id) === String(id));
      const typeId = lookup?.eventTypeId;
      if (!typeId) return;
      const key = String(typeId);
      if (!byType.has(key)) byType.set(key, []);
      byType.get(key).push(Number(id));
    });

    return Array.from(byType.entries()).map(([typeId, involvementIds]) => ({
      typeId: Number(typeId),
      involvementIds
    }));
  }, [filters.eventInvolvementIds, lookups]);

  const eventTypeIdsForQuery = useMemo(() => {
    if (eventInvolvementFilters.length === 0) {
      return (filters.eventTypeIds || []).map(Number);
    }
    const typesWithInvolvements = new Set(eventInvolvementFilters.map((item) => String(item.typeId)));
    return (filters.eventTypeIds || [])
      .filter((typeId) => !typesWithInvolvements.has(String(typeId)))
      .map(Number);
  }, [eventInvolvementFilters, filters.eventTypeIds]);

  const eventStatusIdsForQuery = useMemo(
    () => (filters.eventStatusIds || []).map(Number),
    [filters.eventStatusIds]
  );

  const updateFilters = (patch) => {
    setFilters((previous) => {
      const next = { ...previous, ...patch };
      writeStoredFilters(next);
      return next;
    });
  };

  const handleTypeChange = (nextTypes) => {
    const nextTypeSet = new Set((nextTypes || []).map(String));
    const nextInvolvements = (filters.eventInvolvementIds || []).filter((id) => {
      const lookup = (lookups?.eventInvolvements || []).find((item) => String(item.id) === String(id));
      return !lookup?.eventTypeId || nextTypeSet.size === 0 || nextTypeSet.has(String(lookup.eventTypeId));
    });
    const nextStatuses = (filters.eventStatusIds || []).filter((id) => {
      const lookup = (lookups?.eventStatuses || []).find((item) => String(item.id) === String(id));
      return !lookup?.eventTypeId || nextTypeSet.size === 0 || nextTypeSet.has(String(lookup.eventTypeId));
    });
    updateFilters({
      eventTypeIds: nextTypes || [],
      eventInvolvementIds: nextInvolvements,
      eventStatusIds: nextStatuses
    });
  };

  const handleSearch = () => {
    setSearchLoading(true);
    setAppliedQuery(query.trim());
    setTimeout(() => setSearchLoading(false), 250);
  };

  const handleClearFilters = () => {
    const next = { eventTypeIds: [], eventInvolvementIds: [], eventStatusIds: [] };
    setFilters(next);
    writeStoredFilters(next);
  };

  return (
    <div className="events-page">
      <PullToRefresh onRefresh={handleRefresh}>
        <Card
          title="Active Events Search"
          bordered
          variant="outlined"
          style={{
            margin: '16px 16px 0px 16px',
            backgroundColor: '#ffffff',
            color: '#333',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
          extra={<CalendarOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />}
        >
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Reference / Registration / Fleet / Customer"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onPressEnter={handleSearch}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Event type"
              value={filters.eventTypeIds}
              options={eventTypeOptions}
              onChange={handleTypeChange}
              getPopupContainer={() => document.body}
              style={{ width: '100%' }}
              maxTagCount="responsive"
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Involvement"
              value={filters.eventInvolvementIds}
              options={involvementOptions}
              onChange={(value) => updateFilters({ eventInvolvementIds: value || [] })}
              getPopupContainer={() => document.body}
              style={{ width: '100%' }}
              maxTagCount="responsive"
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Event status"
              value={filters.eventStatusIds}
              options={statusOptions}
              onChange={(value) => updateFilters({ eventStatusIds: value || [] })}
              getPopupContainer={() => document.body}
              style={{ width: '100%' }}
              maxTagCount="responsive"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <Button icon={<CloseCircleOutlined />} onClick={handleClearFilters}>
                Clear Filters
              </Button>
              <Button type="primary" loading={searchLoading} onClick={handleSearch}>
                Search
              </Button>
            </div>
          </Space>
        </Card>

        <EventList
          ref={eventListRef}
          searchTerm={appliedQuery}
          eventTypeIds={eventTypeIdsForQuery}
          eventInvolvementFilters={eventInvolvementFilters}
          eventStatusIds={eventStatusIdsForQuery}
        />
      </PullToRefresh>
    </div>
  );
};

export default Events;
